/**
 * THE REPLAY CLASS of a tool (wayai#5083 Phase 2, proposal §4.5; owner decision 45): what a durable
 * native turn may do with a tool call whose intent its journal holds but whose outcome it does not,
 * because the isolate died between the two. A second axis beside `side_effects`, which decides a
 * live call's spoken confirmation and stays as it is.
 *
 * - `rerun`: run it again. Reads, and internal writes that set an absolute value, so a second run
 *   leaves what the first left.
 * - `keyed`: run it again with the same key, so the system it writes to recognises the repeat. The
 *   key is derived from the journal's step (`effect_ledger`'s `idem_key`). Only a tool whose whole
 *   effect honours the key is `keyed` (owner decision 74): an HTTP tool that sends it in its
 *   `idempotency_header`, and a native tool whose repeat leaves what its first run left. Taking the
 *   key as the id of what a tool creates is not enough while the rest of its effect repeats.
 * - `reconcile`: never run again blindly. Ask the other system what happened where it can say, or
 *   else tell the model the outcome is unknown.
 * - `human`: never automatic: a person decides. Only for a tool a hub admin flags `irreversible`;
 *   no tool defaults to it.
 *
 * The halves that need nothing but the tool live here, as `tool-side-effects.ts` does for the other
 * axis: a platform native tool by its catalog entry, an HTTP tool by its method, a composed chain by
 * its members. The call-level classifier (`toolCallReplay` in `apps/backend/src/tools/replay.ts`)
 * adds what only the dispatcher knows: the connector module, MCP assignments, the tool
 * `execute_tool` forwards to, and a call's arguments where they decide its class (`copy_from_sandbox`
 * writing a toolset document).
 *
 * WHAT IS NOT RECOGNISED IS `reconcile`, never `rerun`: a method, module or name this axis does not
 * know may have acted, and running it again could act twice.
 */

import { NATIVE_TOOLS } from './native-tools';
import { composedChainMemberNames, httpToolSideEffects } from './tool-side-effects';

/** The replay classes, from the least to the most demanding. */
export const TOOL_REPLAY_CLASSES = ['rerun', 'keyed', 'reconcile', 'human'] as const;
export type ToolReplay = (typeof TOOL_REPLAY_CLASSES)[number];

const CATALOG_REPLAY: ReadonlyMap<string, ToolReplay> = new Map(
  NATIVE_TOOLS.map((tool) => [tool.tool_name, tool.replay]),
);

/** The more demanding of two classes, by their order in `TOOL_REPLAY_CLASSES`. */
export function stricterReplay(a: ToolReplay, b: ToolReplay): ToolReplay {
  return TOOL_REPLAY_CLASSES.indexOf(a) >= TOOL_REPLAY_CLASSES.indexOf(b) ? a : b;
}

/**
 * An HTTP tool, by its method: one that reads (`httpToolSideEffects`: GET, HEAD, OPTIONS) runs
 * again. Every other method, and no method (the executor sends it as POST), may have written to a
 * system that would take it twice: `reconcile`, unless the tool sends a key (`withToolReplaySettings`).
 */
export function httpToolReplay(method: unknown): ToolReplay {
  return httpToolSideEffects(method) === 'none' ? 'rerun' : 'reconcile';
}

/** A platform native tool, by the name it is dispatched under; a name the catalog lacks is `reconcile`. */
export function catalogToolReplay(toolName: string): ToolReplay {
  return CATALOG_REPLAY.get(toolName) ?? 'reconcile';
}

/**
 * A member's class within a composed chain, where it differs from the tool's own. A member runs
 * only when its parent's call succeeds and passes `success_path`, so a re-run of the chain is not
 * sure to reach the member's key: `schedule_followup`, `keyed` alone (owner decision 96), keeps a
 * chain `reconcile`, or a re-run whose parent failed would tell the model no follow-up exists while
 * the first one stands.
 */
const CHAIN_MEMBER_REPLAY: ReadonlyMap<string, ToolReplay> = new Map([['schedule_followup', 'reconcile']]);

/**
 * A call raised to its composed chain (`composed_tools`): the chain's members run through the
 * platform native module by name as part of the call, so each is classified from the catalog, as
 * a member (`CHAIN_MEMBER_REPLAY`), and the call takes its strictest member. An unreadable chain is
 * `reconcile`.
 */
export function withComposedChainReplay(own: ToolReplay, composedTools: unknown): ToolReplay {
  const names = composedChainMemberNames(composedTools);
  if (names === undefined) return own;
  if (names === null) return stricterReplay(own, 'reconcile');
  return names.reduce<ToolReplay>(
    (worst, name) => stricterReplay(worst, CHAIN_MEMBER_REPLAY.get(name) ?? catalogToolReplay(name)),
    own,
  );
}

/**
 * THE HUB'S SETTINGS ON A TOOL (`tool.replay_override`, `tool.idempotency_header`,
 * `tool.irreversible`): what an admin knows of a tool that the platform cannot see.
 * - `irreversible`: a person decides, never a re-run: `human`, whatever else holds. Any tool.
 * - `replay_override`: the class of a tool that is not the platform's own (an HTTP or MCP tool),
 *   when its admin knows better than its method or its missing annotations: `rerun` for one that
 *   only reads or writes an absolute value, `reconcile` for one that must never run twice.
 * - `idempotency_header`: an HTTP tool's header for the key (`Idempotency-Key`, say): its writes
 *   become `keyed`, the key being the call's step in the effect ledger, and stay `keyed` under a
 *   `rerun` override (a `reconcile` override wins).
 */
export const REPLAY_OVERRIDES = ['rerun', 'reconcile'] as const;
export type ReplayOverride = (typeof REPLAY_OVERRIDES)[number];

export function isReplayOverride(value: unknown): value is ReplayOverride {
  return (REPLAY_OVERRIDES as readonly unknown[]).includes(value);
}

/** Header names an HTTP tool may not carry its key in: the executor or the platform sets them. */
const RESERVED_HEADERS: ReadonlySet<string> = new Set([
  'authorization', 'proxy-authorization', 'cookie', 'host', 'content-type', 'content-length',
  'transfer-encoding', 'connection', 'accept', 'user-agent',
]);

/** Why `value` cannot name an idempotency header, or null when it can (a header token, not one the platform sets). */
export function idempotencyHeaderRefusal(value: unknown): string | null {
  if (typeof value !== 'string' || !/^[A-Za-z0-9][A-Za-z0-9-]{0,63}$/.test(value)) {
    return 'idempotency_header must be a header name: letters, digits and dashes, at most 64';
  }
  if (RESERVED_HEADERS.has(value.toLowerCase())) {
    return `idempotency_header cannot be ${value}: the platform sets that header`;
  }
  return null;
}

/** The kind of tool the settings apply to: the platform's or a module's own, an MCP tool, an HTTP tool. */
export type ToolSettingsKind = 'native' | 'mcp' | 'http';

/**
 * A tool's own class, adjusted by its hub settings (above). A composed chain raises the result
 * afterwards (`withComposedChainReplay`), so a chain member's class still stands.
 */
export function withToolReplaySettings(
  own: ToolReplay,
  kind: ToolSettingsKind,
  settings: { replay_override?: unknown; idempotency_header?: unknown; irreversible?: unknown },
): ToolReplay {
  if (settings.irreversible === true || settings.irreversible === 1) return 'human';
  // A write that sends a key stays `keyed` under a `rerun` override: its calls still send the key.
  const keyedWrite = kind === 'http' && own === 'reconcile' && idempotencyHeaderRefusal(settings.idempotency_header) === null;
  if (keyedWrite && settings.replay_override !== 'reconcile') return 'keyed';
  if (kind !== 'native' && isReplayOverride(settings.replay_override)) return settings.replay_override;
  return own;
}
